/**
 * Educational Value Proposition Page
 * Built with IBM Carbon Design System for educational decision makers
 * Navigation Flow: Landing → Value Content → ROI Calculator → Success Stories → Contact Sales
 */

import React, { useState } from 'react';
import {
  Grid,
  Column,
  Tile,
  ClickableTile,
  Button,
  ButtonSet,
  Tag,
  Modal,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  ProgressIndicator,
  ProgressStep,
  usePrefix,
  Stack,
  Heading,
  Section,
} from '@carbon/react';
import {
  ArrowRight,
  Education,
  Analytics,
  Time,
  Security,
  ChartLineData,
  Trophy,
  CheckmarkFilled,
  Calculator,
  Document,
  UserMultiple,
  Star,
  Calendar,
} from '@carbon/icons-react';
import { MarketingVariant } from '../../routes/MarketingRouter';
import MarketingHeader from '../../components/marketing/MarketingHeader';
import MarketingFooter from '../../components/marketing/MarketingFooter';
import { useNavigate } from 'react-router-dom';

interface ValuePropositionPageProps {
  variant?: MarketingVariant;
}

interface ValueMetric {
  title: string;
  description: string;
  beforeValue: string;
  afterValue: string;
  improvement: string;
  icon: React.ReactNode;
  details: string[];
}

interface CaseStudy {
  id: string;
  institution: string;
  type: string;
  challenge: string;
  solution: string;
  results: string[];
  metrics: { label: string; value: string; improvement: string }[];
  testimonial: string;
  author: string;
  role: string;
}

const ValuePropositionPage: React.FC<ValuePropositionPageProps> = ({ variant = 'default' }) => {
  const navigate = useNavigate();
  const prefix = usePrefix();
  const [selectedCaseStudy, setSelectedCaseStudy] = useState<CaseStudy | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const getValueMetrics = (): ValueMetric[] => {
    switch (variant) {
      case 'educator':
        return [
          {
            title: 'Lesson Planning Time',
            description: 'AI-powered lesson planning reduces preparation time significantly',
            beforeValue: '6 hours/week',
            afterValue: '1 hour/week',
            improvement: '83% reduction',
            icon: <Time size={32} />,
            details: [
              'Automated curriculum alignment',
              'AI-generated assessment questions',
              'Personalized learning paths',
              'Standards-based lesson templates',
            ],
          },
          {
            title: 'Student Engagement',
            description: 'Personalized learning increases student participation and achievement',
            beforeValue: '65% engaged',
            afterValue: '94% engaged',
            improvement: '29% increase',
            icon: <Education size={32} />,
            details: [
              'Adaptive content difficulty',
              'Interactive multimedia lessons',
              'Real-time progress feedback',
              'Gamified learning experiences',
            ],
          },
          {
            title: 'Grading Efficiency',
            description: 'Automated grading and feedback accelerates assessment workflows',
            beforeValue: '8 hours/week',
            afterValue: '2 hours/week',
            improvement: '75% time saved',
            icon: <Analytics size={32} />,
            details: [
              'Instant auto-grading',
              'Detailed feedback generation',
              'Learning analytics insights',
              'Parent communication automation',
            ],
          },
        ];
      case 'administrator':
        return [
          {
            title: 'Operational Efficiency',
            description: 'Streamlined administrative processes across the district',
            beforeValue: '40 hrs/week',
            afterValue: '25 hrs/week',
            improvement: '37% time saved',
            icon: <ChartLineData size={32} />,
            details: [
              'Automated compliance reporting',
              'Real-time district dashboards',
              'Resource optimization alerts',
              'Data-driven decision making',
            ],
          },
          {
            title: 'Student Outcomes',
            description: 'District-wide improvement in academic performance',
            beforeValue: '72% proficiency',
            afterValue: '85% proficiency',
            improvement: '18% increase',
            icon: <Trophy size={32} />,
            details: [
              'Personalized intervention programs',
              'Early warning systems',
              'Performance tracking analytics',
              'Evidence-based improvements',
            ],
          },
          {
            title: 'Cost Savings',
            description: 'Reduced operational costs through automation and efficiency',
            beforeValue: '$2.8M annually',
            afterValue: '$1.9M annually',
            improvement: '$900K saved',
            icon: <Calculator size={32} />,
            details: [
              'Reduced administrative overhead',
              'Optimized resource allocation',
              'Lower training costs',
              'Decreased manual processes',
            ],
          },
        ];
      default:
        return [
          {
            title: 'Learning Outcomes',
            description: 'Measurable improvement in student achievement across all subjects',
            beforeValue: '68% proficiency',
            afterValue: '87% proficiency',
            improvement: '28% increase',
            icon: <Education size={32} />,
            details: [
              'Personalized learning paths',
              'AI-powered tutoring',
              'Real-time feedback',
              'Adaptive assessments',
            ],
          },
          {
            title: 'Family Engagement',
            description: 'Increased parent participation in educational journey',
            beforeValue: '45% engaged',
            afterValue: '82% engaged',
            improvement: '82% increase',
            icon: <UserMultiple size={32} />,
            details: [
              'Real-time progress updates',
              'Parent-teacher communication',
              'Home learning support',
              'Educational resource sharing',
            ],
          },
          {
            title: 'Time Efficiency',
            description: 'Streamlined educational processes save valuable time',
            beforeValue: '12 hrs/week',
            afterValue: '4 hrs/week',
            improvement: '67% reduction',
            icon: <Time size={32} />,
            details: [
              'Automated administrative tasks',
              'Efficient communication tools',
              'Streamlined reporting',
              'Integrated workflows',
            ],
          },
        ];
    }
  };

  const getCaseStudies = (): CaseStudy[] => {
    return [
      {
        id: 'case-1',
        institution: 'Metro School District',
        type: 'Large Urban District',
        challenge: 'Struggling with declining test scores and low teacher retention',
        solution: 'Implemented Sigma08 AI-powered teaching tools and analytics platform',
        results: [
          'Test scores improved by 23% in first year',
          'Teacher retention increased by 40%',
          'Student engagement up 35%',
          'Administrative time reduced by 50%',
        ],
        metrics: [
          { label: 'Students Impacted', value: '45,000+', improvement: '+100%' },
          { label: 'Schools Connected', value: '150', improvement: 'Full district' },
          { label: 'Annual Savings', value: '$2.3M', improvement: 'ROI: 340%' },
        ],
        testimonial:
          'Sigma08 transformed our entire district. The data-driven insights have revolutionized how we approach education.',
        author: 'Dr. Patricia Williams',
        role: 'Superintendent',
      },
      {
        id: 'case-2',
        institution: 'Lincoln Elementary School',
        type: 'K-5 Elementary',
        challenge: 'Low reading comprehension scores and parent engagement',
        solution: 'Deployed personalized learning platform with family engagement tools',
        results: [
          'Reading scores improved by 45%',
          'Parent engagement increased 300%',
          'Teacher satisfaction up 60%',
          'Homework completion rate: 95%',
        ],
        metrics: [
          { label: 'Student Improvement', value: '45%', improvement: 'Reading scores' },
          { label: 'Parent Portal Usage', value: '89%', improvement: '+300%' },
          { label: 'Teacher Time Saved', value: '8hrs/week', improvement: 'Per teacher' },
        ],
        testimonial: 'The impact on our students has been incredible. Parents are more engaged than ever before.',
        author: 'Sarah Martinez',
        role: '5th Grade Teacher',
      },
      {
        id: 'case-3',
        institution: 'Westfield High School',
        type: '9-12 High School',
        challenge: 'High failure rates in mathematics and science',
        solution: 'Implemented AI tutoring and adaptive learning for STEM subjects',
        results: [
          'Math failure rate dropped 70%',
          'Science proficiency up 55%',
          'College readiness improved 40%',
          'Student confidence increased significantly',
        ],
        metrics: [
          { label: 'Math Proficiency', value: '78%', improvement: '+45%' },
          { label: 'Science Scores', value: '82%', improvement: '+55%' },
          { label: 'College Enrollment', value: '94%', improvement: '+40%' },
        ],
        testimonial:
          'Our students are now confident in STEM subjects. The AI tutoring provides exactly the support they need.',
        author: 'Michael Chen',
        role: 'High School Math Teacher',
      },
    ];
  };

  const valueMetrics = getValueMetrics();
  const caseStudies = getCaseStudies();

  const handleViewCaseStudy = (caseStudy: CaseStudy) => {
    setSelectedCaseStudy(caseStudy);
    setIsModalOpen(true);
  };

  const handleCalculateROI = () => {
    navigate('/calculators');
  };

  const handleScheduleDemo = () => {
    navigate('/demo');
  };

  return (
    <div className="value-proposition-page">
      <MarketingHeader variant={variant} />

      {/* Progress Indicator */}
      <Section className="cds--layout--size-md">
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <ProgressIndicator currentIndex={1} spaceEqually>
              <ProgressStep label="Discover Platform" />
              <ProgressStep label="Explore Value" />
              <ProgressStep label="Calculate ROI" />
              <ProgressStep label="Success Stories" />
              <ProgressStep label="Get Started" />
            </ProgressIndicator>
          </Column>
        </Grid>
      </Section>

      {/* Hero Section */}
      <Section className="cds--layout--size-lg">
        <Grid>
          <Column lg={12} md={6} sm={4}>
            <Stack gap={6}>
              <Heading className="cds--type-heading-06">Measurable Educational Impact</Heading>
              <p className="cds--type-body-long-02">
                See how educational institutions achieve remarkable outcomes with Sigma08's AI-powered learning
                platform. Real data from real schools shows significant improvements in student achievement, educator
                efficiency, and family engagement.
              </p>
              <Tag type="blue" size="lg">
                <CheckmarkFilled /> Evidence-Based Results
              </Tag>
            </Stack>
          </Column>
          <Column lg={4} md={2} sm={4}>
            <Tile className="cds--layout--size-md">
              <Stack gap={4} className="cds--text-align--center">
                <div>
                  <div className="cds--type-heading-05">250K+</div>
                  <div className="cds--type-body-short-01">Students Impacted</div>
                </div>
                <div>
                  <div className="cds--type-heading-04">98%</div>
                  <div className="cds--type-body-short-01">Satisfaction Rate</div>
                </div>
              </Stack>
            </Tile>
          </Column>
        </Grid>
      </Section>

      {/* Value Metrics Section */}
      <Section className="cds--layout--size-lg cds--layer-one">
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <Stack gap={8}>
              <Heading className="cds--type-heading-05 cds--text-align--center">Proven Educational Value</Heading>

              <div className="cds--grid cds--grid--auto-columns">
                {valueMetrics.map((metric, index) => (
                  <Tile key={index} className="cds--layout--size-md">
                    <Stack gap={5}>
                      <div className="cds--flex cds--flex--align-items--center">
                        <div className="cds--spacing-right-05 cds--text-primary">{metric.icon}</div>
                        <h3 className="cds--type-heading-04">{metric.title}</h3>
                      </div>

                      <p className="cds--type-body-long-01">{metric.description}</p>

                      <div className="cds--flex cds--flex--justify-content--space-between">
                        <div>
                          <div className="cds--type-helper-text-01">Before</div>
                          <div className="cds--type-heading-03 cds--text-error">{metric.beforeValue}</div>
                        </div>
                        <div>
                          <div className="cds--type-helper-text-01">After</div>
                          <div className="cds--type-heading-03 cds--text-success">{metric.afterValue}</div>
                        </div>
                      </div>

                      <Tag type="green" size="md">
                        {metric.improvement}
                      </Tag>

                      <ul className="cds--list--unordered">
                        {metric.details.map((detail, i) => (
                          <li key={i} className="cds--flex cds--flex--align-items--center cds--spacing-bottom-03">
                            <CheckmarkFilled size={16} className="cds--spacing-right-03 cds--text-success" />
                            <span className="cds--type-body-short-01">{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </Stack>
                  </Tile>
                ))}
              </div>
            </Stack>
          </Column>
        </Grid>
      </Section>

      {/* Success Stories Preview */}
      <Section className="cds--layout--size-lg">
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <Stack gap={8}>
              <div className="cds--text-align--center">
                <Heading className="cds--type-heading-05">Real Success Stories</Heading>
                <p className="cds--type-body-long-01 cds--spacing-top-05">
                  Learn how educational institutions transformed their outcomes with Sigma08
                </p>
              </div>

              <div className="cds--grid cds--grid--auto-columns">
                {caseStudies.map((caseStudy, index) => (
                  <ClickableTile
                    key={caseStudy.id}
                    onClick={() => handleViewCaseStudy(caseStudy)}
                    className="cds--layout--size-md"
                  >
                    <Stack gap={4}>
                      <div>
                        <h3 className="cds--type-heading-03 cds--spacing-bottom-03">{caseStudy.institution}</h3>
                        <Tag type="blue" size="sm">
                          {caseStudy.type}
                        </Tag>
                      </div>

                      <p className="cds--type-body-short-01">{caseStudy.challenge}</p>

                      <div className="cds--flex cds--flex--gap-05">
                        {caseStudy.metrics.slice(0, 2).map((metric, i) => (
                          <div key={i} className="cds--text-align--center">
                            <div className="cds--type-heading-04 cds--text-primary">{metric.value}</div>
                            <div className="cds--type-helper-text-01">{metric.label}</div>
                          </div>
                        ))}
                      </div>

                      <Button kind="ghost" size="sm" renderIcon={ArrowRight}>
                        Read Full Case Study
                      </Button>
                    </Stack>
                  </ClickableTile>
                ))}
              </div>
            </Stack>
          </Column>
        </Grid>
      </Section>

      {/* CTA Section */}
      <Section className="cds--layout--size-lg cds--layer-accent-01">
        <Grid>
          <Column lg={12} md={6} sm={4}>
            <Stack gap={5}>
              <Heading className="cds--type-heading-05 cds--text-inverse">Ready to See Your Impact?</Heading>
              <p className="cds--type-body-long-01 cds--text-inverse">
                Calculate your specific ROI and see how Sigma08 can transform your educational outcomes.
              </p>
            </Stack>
          </Column>
          <Column lg={4} md={2} sm={4}>
            <ButtonSet stacked>
              <Button kind="secondary" size="lg" renderIcon={Calculator} onClick={handleCalculateROI}>
                Calculate Your ROI
              </Button>
              <Button kind="tertiary" size="lg" renderIcon={Calendar} onClick={handleScheduleDemo}>
                Schedule Demo
              </Button>
            </ButtonSet>
          </Column>
        </Grid>
      </Section>

      {/* Case Study Modal */}
      <Modal
        open={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        modalHeading={selectedCaseStudy?.institution}
        modalLabel="Success Story"
        primaryButtonText="Schedule Demo"
        secondaryButtonText="Close"
        onRequestSubmit={handleScheduleDemo}
        size="lg"
      >
        {selectedCaseStudy && (
          <Stack gap={6} className="cds--layout--size-md">
            <Tag type="blue" size="md">
              {selectedCaseStudy.type}
            </Tag>

            <div>
              <h4 className="cds--type-heading-03 cds--spacing-bottom-03">Challenge</h4>
              <p className="cds--type-body-long-01">{selectedCaseStudy.challenge}</p>
            </div>

            <div>
              <h4 className="cds--type-heading-03 cds--spacing-bottom-03">Solution</h4>
              <p className="cds--type-body-long-01">{selectedCaseStudy.solution}</p>
            </div>

            <div>
              <h4 className="cds--type-heading-03 cds--spacing-bottom-05">Key Metrics</h4>
              <div className="cds--grid cds--grid--auto-columns">
                {selectedCaseStudy.metrics.map((metric, i) => (
                  <Tile key={i} className="cds--text-align--center cds--layout--size-sm">
                    <Stack gap={3}>
                      <div className="cds--type-heading-04 cds--text-primary">{metric.value}</div>
                      <div className="cds--type-helper-text-01">{metric.label}</div>
                      <Tag type="green" size="sm">
                        {metric.improvement}
                      </Tag>
                    </Stack>
                  </Tile>
                ))}
              </div>
            </div>

            <div>
              <h4 className="cds--type-heading-03 cds--spacing-bottom-03">Results</h4>
              <ul className="cds--list--unordered">
                {selectedCaseStudy.results.map((result, i) => (
                  <li key={i} className="cds--type-body-short-01">
                    {result}
                  </li>
                ))}
              </ul>
            </div>

            <blockquote className="cds--quote cds--layout--size-sm">
              <p className="cds--type-body-long-01">"{selectedCaseStudy.testimonial}"</p>
              <footer className="cds--type-body-short-01 cds--spacing-top-03">
                <strong>{selectedCaseStudy.author}</strong>, {selectedCaseStudy.role}
              </footer>
            </blockquote>
          </Stack>
        )}
      </Modal>

      <MarketingFooter />
    </div>
  );
};

export default ValuePropositionPage;
